/* eslint-disable max-len */
import Vue from 'vue'
import Vuex, { Store } from 'vuex'
import VueRouter from 'vue-router'
import { bookingRoutes, BOOKING_ROUTE_NAMES } from '@components/04.booking/booking-wizard/routes/booking-wizard-routes'
import { bookingModifyRoutes, BOOKING_MODIFY_ROUTE_NAMES } from '@components/06.modify-reservation/routes/modify-booking-routes'
import { spaSoftRoutes, SPA_SOFT_ROUTE_NAMES } from '@components/07.spa-soft/spa-soft/routes/spa-soft-routes'
import Plugins from '@plugins/index'
import { VueSitecoreMixin, SitecoreExtend } from '@lib/vue-scexpeditor'
import directives from '@directives'
import components from '@components'
import {
  // Directives
  VClosePopper,
  // Components
  Dropdown,
} from 'floating-vue'
import { track } from '@plugins/tracking'
import StoreConfig from './store'
// Jquery ui CSS
import 'webpack-jquery-ui/css'

// The main CSS entry point, it will be placed in a separate file.
import '../02.styles/index.scss'
import 'floating-vue/dist/style.css'

const vueOpts = {
  mixins: []
}

Vue.directive('close-popper', VClosePopper)
Vue.component('VDropdown', Dropdown)

// Install Router (used for booking wizard component)
const hasRouter = document.querySelector('input[data-router="true"]')

if (hasRouter) {
  Vue.use(VueRouter)

  // Exclude paths that are adding hashes to the end of the URL
  const excludedPaths = ['/gallery', '/explore', '/summer'];

  const getRouterMode = () => excludedPaths.some(path => window.location.pathname.includes(path)) ? 'history' : 'hash';

  const isBookingWizardRoute = (window.location.pathname.toLowerCase().includes('/booking-wizard') || window.location.pathname.toLowerCase().includes('/reservation'))
  const isBookingModifyRoute = (window.location.pathname.toLowerCase().includes('/modify') || window.location.pathname.toLowerCase().includes('/modify-reservation-main'))
  const isSpaSoftRoute = (window.location.pathname.toLowerCase().includes('/spa-soft-booking') || window.location.pathname.toLowerCase().includes('/spa-reservation'))

  vueOpts.router = new VueRouter({
    mode: getRouterMode(),
    routes: [
      {
        path: '/',
        name: 'root',
        component: {
          template: '<router-view/>',
        },
        redirect: (to) => {
          if (isBookingWizardRoute && !to.path.toLowerCase().includes('booking')) {
            return { name: BOOKING_ROUTE_NAMES.ROOM_LIST, query: to.query }
          }
          if (isBookingModifyRoute && !to.path.toLowerCase().includes('find-reservation')) {
            return { name: BOOKING_MODIFY_ROUTE_NAMES.LOOKUP_RESERVATION }
          }
          if (isSpaSoftRoute && !to.path.toLowerCase().includes('spa-soft')) {
            return { name: SPA_SOFT_ROUTE_NAMES.SPA_SOFT_TREATMENTS_AND_DATE, query: to.query }
          }
          return { name: null }
        }
      },
      ...bookingRoutes,
      ...bookingModifyRoutes,
      ...spaSoftRoutes
    ],
    scrollBehavior(to) {
      if (to?.query?.hotelCode || isSpaSoftRoute) {
        window.scroll({
          top: 0,
          left: 0
        })
      }
    }
  })

  vueOpts.router.afterEach((to, from) => {
    // List of vue-router routes to not auto-track
    // example: we don't want to auto-track Booking Modify Routes to avoid duplications,
    // because they have custom tracking inside .vue files.
    // Spa Soft routes is disabled for now, we will have a ticket for this in the future.
    const untrackedRoutes = [
      ...Object.values(BOOKING_MODIFY_ROUTE_NAMES),
      ...Object.values(SPA_SOFT_ROUTE_NAMES),
    ]
    // List of ignored route names to not include in the tracking data
    // In this case we don't want to show the 'room-list' in page name
    const ignoredNames = [BOOKING_ROUTE_NAMES.ROOM_LIST]

    const isBookingRoute = to.path.startsWith(BOOKING_ROUTE_NAMES.ROOT)
    const bookingMode = to.query?.bookingEditMode === 'true' ? 'modify' : 'reservation'

    let pageName = to.name
    let siteSection = null

    if (isBookingRoute) {
      siteSection = to.query?.bookingEditMode === 'true' ? 'modify cancel' : 'booking engine'
      pageName = !ignoredNames.includes(to.name) ? `home >> ${bookingMode} >> ${to.name}` : `home >> ${bookingMode}`
    }

    if (!untrackedRoutes.includes(to.name) && from.name !== to.name) {
      track({
        page: {
          name: pageName,
          hotelCode: to.query?.hotelCode,
          siteSection,
        },
      })
    }
  })
}

// Install plugins
Object.values(Plugins).forEach((Plugin) => Vue.use(Plugin))

// Add the Vuex plugin.
Vue.use(Vuex)

// Create store
vueOpts.store = new Store(StoreConfig)

// Bind all global directives.
directives.forEach((directive) => Vue.directive(directive.name, directive))

// Register all components globally.
Object.values(components).forEach((component) => Vue.component(component.name, component))

// Mixin for sitecore's ExpEditor compatibility
Vue.use(VueSitecoreMixin)

// The root Vue instance.
const root = new Vue(vueOpts)

// Extend sitecore's ExpEditor javascript for rendering vue components on update
Vue.use(SitecoreExtend, root)

if (process.env.NODE_ENV.trim() === 'production') {
  Vue.config.devtools = false
  Vue.config.debug = false
  Vue.config.silent = true
}

// Mount the Vue application.
root.$mount('#app')
